import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchTopology } from '@/lib/api';
import { StatusDot } from '@/components/shared/StatusDot';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Network, Server, Database, Globe, Cpu, Cloud, ArrowRight } from 'lucide-react';

const CI_ICONS = {
  SERVER: Server, DATABASE: Database, NETWORK_DEVICE: Network,
  APPLICATION: Globe, CLOUD_SERVICE: Cloud, INFRASTRUCTURE: Cpu,
};

const REL_COLORS = {
  DEPENDS_ON: { color: '#EF4444', label: 'Depends On' },
  CONNECTED_TO: { color: '#F59E0B', label: 'Connected To' },
  HOSTED_ON: { color: '#06B6D4', label: 'Hosted On' },
  MONITORS: { color: '#6366F1', label: 'Monitors' },
  ROUTES_TO: { color: '#10B981', label: 'Routes To' },
};

const ENV_COLORS = { PROD: '#EF4444', STAGING: '#F59E0B', DEV: '#6366F1' };

export default function TopologyPage() {
  const { data: topology, isLoading } = useQuery({ queryKey: ['topology'], queryFn: fetchTopology });
  const [selectedNode, setSelectedNode] = useState(null);
  const [filterRel, setFilterRel] = useState(null);

  if (isLoading) return (
    <div className="w-full max-w-[1600px] mx-auto">
      <div className="h-8 w-48 bg-[var(--nexus-bg-elevated)] rounded animate-pulse mb-4" />
      <div className="h-[500px] bg-[var(--nexus-bg-elevated)] rounded-xl animate-pulse" />
    </div>
  );

  const nodes = topology?.nodes || [];
  const edges = topology?.edges || [];

  const selectedEdges = selectedNode
    ? edges.filter(e => e.source === selectedNode || e.target === selectedNode)
    : edges;
  const filteredEdges = filterRel ? selectedEdges.filter(e => e.type === filterRel) : selectedEdges;
  const connectedIds = selectedNode ? new Set([selectedNode, ...filteredEdges.map(e => e.source), ...filteredEdges.map(e => e.target)]) : null;

  // Group edges by type for display
  const edgesByType = {};
  filteredEdges.forEach(e => { if (!edgesByType[e.type]) edgesByType[e.type] = []; edgesByType[e.type].push(e); });

  return (
    <div data-testid="topology-page" className="w-full max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">CMDB Topology</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">{nodes.length} configuration items, {edges.length} relationships</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedNode && <button onClick={() => setSelectedNode(null)} className="text-xs text-[var(--nexus-accent)] hover:underline">Clear selection</button>}
          <Link to="/assets" className="text-xs text-[var(--nexus-text-muted)] hover:text-[var(--nexus-text-secondary)]">Back to Assets</Link>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        {Object.entries(REL_COLORS).map(([type, conf]) => (
          <button key={type} onClick={() => setFilterRel(filterRel === type ? null : type)}
            className={`flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-md border transition-colors ${
              filterRel === type ? 'border-white/20 bg-white/[0.06]' : 'border-[var(--nexus-border)] hover:bg-white/[0.03]'
            }`}>
            <span className="w-3 h-0.5 rounded" style={{ backgroundColor: conf.color }} />
            <span className="text-[var(--nexus-text-muted)]">{conf.label}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Node grid */}
        <div className="lg:col-span-2">
          <div className="nexus-card relative overflow-hidden" data-testid="topology-graph">
            <div className="p-3 border-b border-[var(--nexus-border)]">
              <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Configuration Items</h2>
            </div>
            <div className="p-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {nodes.map(node => {
                const Icon = CI_ICONS[node.type] || Server;
                const isSelected = selectedNode === node.id;
                const isDim = connectedIds && !connectedIds.has(node.id);
                return (
                  <button key={node.id} onClick={() => setSelectedNode(isSelected ? null : node.id)}
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      isSelected ? 'border-[var(--nexus-accent)] bg-[var(--nexus-accent)]/10 animate-nexus-breathe' :
                      isDim ? 'border-[var(--nexus-border)] opacity-30' :
                      'border-[var(--nexus-border)] bg-[var(--nexus-bg-base)] hover:bg-white/[0.03]'
                    }`} data-testid={`topo-node-${node.name}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={12} className="text-[var(--nexus-accent)]" />
                      <StatusDot status={node.status === 'ACTIVE' ? 'active' : node.status === 'MAINTENANCE' ? 'warning' : 'idle'} pulse={node.status === 'ACTIVE' && isSelected} size="sm" />
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ENV_COLORS[node.environment] || '#475569' }} />
                    </div>
                    <p className="font-mono text-[10px] text-[var(--nexus-text-primary)] truncate">{node.name}</p>
                    <p className="text-[9px] text-[var(--nexus-text-muted)]">{node.type} | {node.environment}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Relationships panel */}
        <div>
          <div className="nexus-card relative overflow-hidden">
            <div className="p-3 border-b border-[var(--nexus-border)]">
              <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">
                {selectedNode ? `Relationships for ${nodes.find(n => n.id === selectedNode)?.name || '?'}` : 'All Relationships'}
              </h2>
            </div>
            <ScrollArea className="h-[450px]">
              <div className="p-2 space-y-3">
                {Object.entries(edgesByType).map(([type, typeEdges]) => {
                  const conf = REL_COLORS[type] || { color: '#475569', label: type };
                  return (
                    <div key={type}>
                      <div className="flex items-center gap-1.5 mb-1.5 px-1">
                        <span className="w-4 h-0.5 rounded" style={{ backgroundColor: conf.color }} />
                        <span className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">{conf.label} ({typeEdges.length})</span>
                      </div>
                      <div className="space-y-1">
                        {typeEdges.map((edge, i) => (
                          <div key={i} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                            <span className="font-mono text-[10px] text-[var(--nexus-text-secondary)]">{edge.source_name}</span>
                            <ArrowRight size={10} style={{ color: conf.color }} />
                            <span className="font-mono text-[10px] text-[var(--nexus-accent)]">{edge.target_name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {Object.keys(edgesByType).length === 0 && (
                  <div className="text-center py-8 text-[var(--nexus-text-muted)] text-xs">
                    {selectedNode ? 'No relationships for this CI' : 'No relationships found'}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  );
}
